import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../../api/axios";
import MainLayout from "../layoutpage/MainLayout";
import SuccessModal from "../../components/SuccessModal";
import Footer from "../../components/Footer";

export default function UnitDetailsPage({ user, onLogout }) {
  const { id } = useParams(); // This is the unit ID
  const navigate = useNavigate();
  const [unit, setUnit] = useState(null);
  const [property, setProperty] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  user = JSON.parse(localStorage.getItem("user"));

  const fetchProperty = useCallback(async (propertyId) => {
    try {
      const res = await api.get(`/api/properties/${propertyId}`);
      setProperty(res.data);
    } catch (error) {
      console.log("Property fetch failed", error);
    }
  }, []);

  const fetchUnitDetails = useCallback(async () => {
    try {
      const res = await api.get(`/api/units/${id}`);
      const unitData = res.data.data;
      setUnit(unitData);
      console.log("Unit details", unitData);

      // Fetch property details if unit has property_id
      if (unitData.property_id) {
        fetchProperty(unitData.property_id);
      }
    } catch (error) {
      console.log("Unit fetch failed", error);
    }
  }, [id, fetchProperty]);

  useEffect(() => {
    fetchUnitDetails();
  }, [id, fetchUnitDetails]);

  //  Open Modal
  const openBookingModal = () => {
    setShowModal(true);
  };

  //  Close Modal
  const closeModal = () => {
    setShowModal(false);
  };

  //  Booking API
  const handleBookingSubmit = async () => {
    const today = new Date();
    const checkInDate = today.toISOString().split("T")[0];

    const checkoutDate = new Date(today);
    checkoutDate.setDate(today.getDate() + 3);
    const checkOutDate = checkoutDate.toISOString().split("T")[0];

    try {
      await api.post("/api/bookings", {
        property_id: unit.property_id,
        unit_id: unit.id,
        check_in_date: checkInDate,
        check_out_date: checkOutDate,
      });

      setShowSuccessModal(true);
      closeModal();
      fetchUnitDetails();
    } catch (error) {
      console.log(error);
      alert(error.response?.data?.message ?? "Booking failed");
    }
  };

  if (!unit) return <div className="text-center mt-5">Loading...</div>;

  return (
    <>
      <div className="container-fluid">
        <button className="btn btn-secondary mb-3" onClick={() => navigate(-1)}>
          &larr; Back
        </button>

        <div className="card shadow-lg border-0 rounded-4 p-4">
          <div className="row g-4">
            {/* Left Side - Photos (same pattern as UnitQuickViewModal) */}
            <div className="col-md-6">
              {unit.photos && unit.photos.length > 0 ? (
                <>
                  <div
                    className="border rounded overflow-hidden bg-light d-flex align-items-center justify-content-center"
                    style={{ minHeight: "400px" }}
                  >
                    <img
                      src={
                        (unit.photos.find((p) => p.is_primary) || unit.photos[0])
                          .file_path.startsWith("http")
                          ? (unit.photos.find((p) => p.is_primary) || unit.photos[0])
                              .file_path
                          : `${api.defaults.baseURL}/${(unit.photos.find((p) => p.is_primary) || unit.photos[0]).file_path}`
                      }
                      alt={`Unit ${unit.unit_number}`}
                      className="img-fluid"
                      style={{
                        maxHeight: "500px",
                        objectFit: "cover",
                        width: "100%",
                      }}
                    />
                  </div>
                  {unit.photos.length > 1 && (
                    <div className="row g-2 mt-2">
                      {unit.photos.slice(0, 4).map((photo, index) => (
                        <div key={photo.id || index} className="col-3">
                          <img
                            src={
                              photo.file_path.startsWith("http")
                                ? photo.file_path
                                : `${api.defaults.baseURL}/${photo.file_path}`
                            }
                            alt={`Unit ${unit.unit_number} view ${index + 1}`}
                            className="img-fluid rounded"
                            style={{
                              height: "60px",
                              width: "100%",
                              objectFit: "cover",
                              border: "1px solid #eee",
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div
                  className="border rounded overflow-hidden bg-light d-flex align-items-center justify-content-center"
                  style={{ minHeight: "400px" }}
                >
                  <span className="text-muted">No Photo Available</span>
                </div>
              )}
            </div>

            {/* Right Side - Details */}
            <div className="col-md-6">
              <h2 className="fw-bold mb-3">Unit {unit.unit_number}</h2>
              {property && (
                <h5 className="text-muted mb-4">in {property.name}</h5>
              )}

              <h3 className="text-success fw-bold mb-4">
                ৳ {Number(unit.price).toLocaleString()}
              </h3>

              <dl className="row mb-4">
                <dt className="col-sm-4">Floor</dt>
                <dd className="col-sm-8">{unit.floor}</dd>

                <dt className="col-sm-4">Type</dt>
                <dd className="col-sm-8">{unit.type?.name || "—"}</dd>

                <dt className="col-sm-4">Status</dt>
                <dd className="col-sm-8">
                  <span
                    className={`badge ${
                      unit.status?.status_type === "available"
                        ? "bg-success"
                        : "bg-secondary"
                    }`}
                  >
                    {unit.status?.status_type || "—"}
                  </span>
                </dd>

                <dt className="col-sm-4">Area</dt>
                <dd className="col-sm-8">
                  {unit.measurement?.length > 0
                    ? `${unit.measurement[0].area_sq_ft} ${unit.measurement[0].unit_of_measure}`
                    : unit.measurement?.area_sq_ft
                    ? `${unit.measurement.area_sq_ft} ${unit.measurement.unit_of_measure}`
                    : "N/A"}
                </dd>

                {unit.owner && (
                  <>
                    <dt className="col-sm-4">Owner</dt>
                    <dd className="col-sm-8">{unit.owner.name}</dd>
                  </>
                )}

                {unit.manager && (
                  <>
                    <dt className="col-sm-4">Manager</dt>
                    <dd className="col-sm-8">{unit.manager.name}</dd>
                  </>
                )}
              </dl>

              {/* Facilities / Description if available */}
              {unit.facilities && (
                <div className="mb-4">
                  <h5 className="fw-bold">Facilities</h5>
                  <p className="text-muted">
                    {Array.isArray(unit.facilities)
                      ? unit.facilities.join(", ")
                      : typeof unit.facilities === "object"
                      ? Object.values(unit.facilities).join(", ")
                      : unit.facilities}
                  </p>
                </div>
              )}

              {unit.description && (
                <div className="mb-4">
                  <h5 className="fw-bold">Description</h5>
                  <p className="text-muted">{unit.description}</p>
                </div>
              )}

              <div className="d-grid gap-2 ">
                {user?.id &&
                unit.bookings?.length > 0 &&
                user?.id === unit.bookings[0]?.user_id ? (
                  <Link
                    to={`/properties_available_booking/${unit.bookings[0].id}`}
                    className="btn btn-info btn-lg d-flex justify-content-center align-items-center"
                  >
                    Check In
                  </Link>
                ) : (
                  <button
                    className="btn btn-primary btn-lg d-flex justify-content-center align-items-center"
                    onClick={openBookingModal}
                    disabled={
                      unit.bookings?.length > 0 &&
                      Number(user?.id) !== Number(unit.bookings[0]?.user_id)
                    }
                  >
                    {unit.bookings?.length > 0 &&
                    Number(user?.id) !== Number(unit.bookings[0]?.user_id)
                      ? " Not Available "
                      : "Book Now"}
                    {/* {unit.status?.status_type === 'available' ? 'Book Now' : 'Not Available'} */}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* MODAL */}
        {showModal && (
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content shadow-lg">
                <div className="modal-header bg-primary text-white">
                  <h5 className="modal-title">
                    Booking Unit: {unit.unit_number}
                  </h5>
                  <button
                    className="btn-close btn-close-white"
                    onClick={closeModal}
                  ></button>
                </div>

                <div className="modal-body">
                  <p className="fs-5">
                    You are going to book the property now. It will be reserved
                    for you until{" "}
                    <strong>
                      {new Date(
                        new Date().setDate(new Date().getDate() + 3)
                      ).toDateString()}
                    </strong>
                    . You must finish paperwork and payment before that.
                  </p>
                </div>

                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={closeModal}>
                    Cancel
                  </button>

                  <button
                    className="btn btn-primary px-4"
                    onClick={handleBookingSubmit}
                  >
                    Confirm
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <SuccessModal
          show={showSuccessModal}
          onClose={() => setShowSuccessModal(false)}
          message="Booking Successful! Please proceed with payment."
        />
      </div>
    </>
  );
}
